import React, { useContext, useEffect, useState } from 'react'
import { getValueForm } from '@App/helpers/get-value-form'
import { formFieldsInvalidCheck } from '@App/helpers/work-entities/entities-validators'
import { StoresContext } from '@App/contexts'
import { observer } from 'mobx-react'
import RadioSelectionTab from '@Components/WorkRegistrationSubs/AddWriter/RadioSelectionTab'
import WriterAfterFindForm from '@Components/WorkRegistrationSubs/AddWriter/WriterAfterFindForm'
import { WriterSearch } from '@App/stores/search/models/writer-search'
import WriterCreateSubForm from '@Components/WorkRegistrationSubs/AddWriter/WriterCreateForm/WriterCreateSubForm'
import { WriterModel } from '@Entities/work/writer-model'

interface WriterCreateFormProps {
    writerData: WriterModel
    setWriterData: (writerData: WriterModel) => void
    addWriterToDraft: (closePopup: boolean) => void
    onRadioButtonClicked: () => void
    setFoundWriterToDraft: (writer: WriterModel, closePopup: boolean) => Promise<void>
    expanded?: boolean
}

export interface WriterCreateFormValidations {
    [key: string]: {
        maxLength: number
        error: string
        required: boolean
        isIPI?: boolean
    }
}

const WriterCreateForm = ({
    writerData,
    setWriterData,
    addWriterToDraft,
    onRadioButtonClicked,
    expanded,
    setFoundWriterToDraft,
}: WriterCreateFormProps) => {
    const [validations, setValidation] = useState<WriterCreateFormValidations>({
        firstName: {
            maxLength: 60,
            error: '',
            required: false,
        },
        lastName: {
            required: true,
            maxLength: 200,
            error: '',
        },
        roleCode: {
            maxLength: 60,
            error: '',
            required: true,
        },
        ipiNumber: {
            isIPI: true,
            maxLength: 11,
            error: '',
            required: false,
        },
    })

    const [showWriterWasFoundForm, setShowWriterWasFoundForm] = useState<boolean | 'remainOpen'>(false)
    const [isDisabled, setIsDisabled] = useState<boolean>()
    const { writerSearchStore, preloaderStore } = useContext(StoresContext)

    const getValues = (value: string | number | boolean | null, id: string) => {
        const data = getValueForm(value, id, writerData, validations)
        setValidation(data.validationObject)
        setIsDisabled(formFieldsInvalidCheck(data.formObject, data.validationObject))

        setWriterData({ ...data.formObject })
    }

    const { firstName, lastName, ipiNumber, roleCode } = writerData

    const checkCreatingWriter = async (closePopup: boolean) => {
        preloaderStore.openTransparentPreloader(true)
        const searchObj: WriterSearch = {
            fullName: `${firstName} ${lastName}`,
            ipiNameNumber: ipiNumber ?? undefined,
        }

        writerSearchStore.setWriterSearch(searchObj)
        await writerSearchStore.searchWriters()

        if (writerSearchStore.isThereSearchResults) {
            setShowWriterWasFoundForm(closePopup ? true : 'remainOpen')
            preloaderStore.openTransparentPreloader(false)
            return
        }

        doAddWriterToDraft(closePopup)
        preloaderStore.openTransparentPreloader(false)
    }

    const doAddWriterToDraft = (closePopup: boolean) => {
        addWriterToDraft(closePopup)
        if (!closePopup) {
            setShowWriterWasFoundForm(false)
        }
    }

    const doSetWriterToDraft = async (writer: WriterModel, closePopup: boolean) => {
        await setFoundWriterToDraft({ ...writer, roleCode }, closePopup)
        if (!closePopup) {
            setShowWriterWasFoundForm(false)
        }
    }

    useEffect(() => {
        setIsDisabled(formFieldsInvalidCheck(writerData, validations))
        getValues(firstName, 'firstName')
    }, [])

    useEffect(() => {
        setIsDisabled(formFieldsInvalidCheck(writerData, validations))
    }, [writerData])

    return (
        <RadioSelectionTab
            id="create-writer-tab"
            name="Create a writer"
            subTitle={
                !showWriterWasFoundForm
                    ? 'If you use only one name or a pseudonym for your writer name, enter that name in the Last Name field and leave the First Name field blank.'
                    : undefined
            }
            onClick={onRadioButtonClicked}
            forceChecked={expanded}
        >
            {expanded && (
                <>
                    {!showWriterWasFoundForm ? (
                        <WriterCreateSubForm
                            firstName={firstName}
                            lastName={lastName}
                            ipiNumber={ipiNumber}
                            roleCode={roleCode}
                            validations={validations}
                            getValues={getValues}
                            onClick={checkCreatingWriter}
                            buttonsDisabled={isDisabled ?? false}
                        />
                    ) : (
                        <WriterAfterFindForm
                            changeFormTo={() => {
                                setShowWriterWasFoundForm(false)
                            }}
                            setFoundWriterToDraft={(foundWriter) =>
                                doSetWriterToDraft(foundWriter, showWriterWasFoundForm !== 'remainOpen')
                            }
                            addWriterToDraft={() => doAddWriterToDraft(showWriterWasFoundForm !== 'remainOpen')}
                        />
                    )}
                </>
            )}
        </RadioSelectionTab>
    )
}

export default observer(WriterCreateForm)
